import { Field, Form, Formik, FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"

import { IProjectReport } from "@api/schema"
import { FormButtonArea } from "@components/common/form/FormButtonArea"
import FormikInput from "@components/common/form/FormikInput"
import GeneralFormError from "@components/common/form/GeneralFormError"
import { IFormikActions } from "@redux/helper/actions"

interface IProps {
  onSubmit: (...args: any) => void
  toggle: () => void
}

/**
 * provides a form to report the misuse of a project
 */
const ReportProjectForm: React.FC<IProps> = ({ onSubmit, toggle }: IProps) => {
  const { t } = useTranslation("common")

  const validate = (values: IProjectReport): FormikErrors<IProjectReport> => {
    const errors: { [key: string]: string } = {}

    if (values.reportMessage.trim().length < 5) {
      errors.reportMessage = "validate.general.tooShort"
    } else if (values.reportMessage.trim().length > 1000) {
      errors.reportMessage = "validate.general.tooLong"
    }

    if (values.reporterName.trim().length > 50) {
      errors.reporterName = "validate.general.tooLong"
    }

    return errors
  }

  const onFormSubmit = (values: IProjectReport, actions: IFormikActions): void => {
    // remove trailing spaces
    values.reportMessage = values.reportMessage.trim()
    // @todo Backend remove the unnecessary length constraints for the email.
    // Then the null check can be removed, cause the initial email value can be an empty string
    if (values.reporterEmail?.length === 0) {
      values.reporterEmail = null
    } else if (values.reporterEmail) {
      values.reporterEmail = values.reporterEmail.trim()
    }
    values.reporterName = values.reporterName.trim()

    onSubmit(values, actions)
  }

  return <Formik<IProjectReport>
    initialValues={{ reportMessage: "", reporterName: "", reporterEmail: null }}
    onSubmit={onFormSubmit}
    validate={validate}
    validateOnBlur={false}
    validateOnChange={false}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting,
      values,
    }) => <Form onSubmit={handleSubmit}>
        <GeneralFormError
          errors={errors}
          prefix="project"
          excludeFields={["reportMessage", "reporterName", "reporterEmail"]}
        />

        <Field component={FormikInput}
          help="form.projectReport.reportMessage.help"
          label="form.projectReport.reportMessage.label"
          name="reportMessage"
          placeholder="form.projectReport.reportMessage.placeholder"
          required
          type="textarea"
          maxLength={1000}
          value={values.reportMessage}
        />

        <Field component={FormikInput}
          help="form.projectReport.reporterName.help"
          label="form.projectReport.reporterName.label"
          name="reporterName"
          placeholder="form.projectReport.reporterName.placeholder"
          type="text"
          value={values.reporterName}
        />

        <Field component={FormikInput}
          help="form.projectReport.reporterEmail.help"
          label="form.projectReport.reporterEmail.label"
          name="reporterEmail"
          placeholder="form.projectReport.reporterEmail.placeholder"
          type="email"
          value={values.reporterEmail}
        />

        <p className="text-danger">{t("form.requiredFieldsHint")}</p>

        <FormButtonArea
          isSubmitting={isSubmitting}
          submitButton={{
            title: "form.submit",
            iconName: "message"
          }}
          cancelButton={{ onClick: toggle }}
        />
      </Form>
    }
  </Formik>
}

export default ReportProjectForm
